/**
 * Field name mapping for backward compatibility (matches backend logic)
 */
const FIELD_NAME_MAPPING = {
  patientdob: "patientDOB",
  patientmrn: "patientMRN",
}

const REDACTABLE_INTAKE_QUESTION_TYPES = ["text", "date", "phone", "email", "number", "ssn"]

/**
 * Creates a mapping of questionId -> answer from answerFieldName values
 * This provides backward compatibility with the old interaction-based storage
 *
 * @param {Array} intakeQuestions - Array of intake question objects
 * @param {Object} formFields - Object containing form field values
 * @returns {Object} Mapping of questionId to answer value
 */
export const createAnswersFromFieldNames = (intakeQuestions, formFields) => {
  if (!intakeQuestions || intakeQuestions.length === 0) {
    return {}
  }

  const {
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    providerFirstName,
    providerLastName,
    phoneNumberAC,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
  } = formFields

  const formFieldMap = {
    patientFirstName,
    patientLastName,
    patientMRN,
    patientDOB,
    patientMrn: patientMRN,
    patientDob: patientDOB,
    providerFirstName,
    providerLastName,
    phoneNumberAC,
    bchOaklandHospitalConfirmation,
    location,
    memberIdNumber,
    nyuAccessCode,
    sentaraAccessCode,
    visitId,
    callerFirstName,
    callerLastName,
  }

  const answers = {}
  intakeQuestions.forEach((question) => {
    if (!question.answerFieldName) return
    const fieldName = FIELD_NAME_MAPPING[question.answerFieldName.toLowerCase()] || question.answerFieldName
    const value = formFieldMap[fieldName]
    if (value !== undefined) {
      answers[question.id] = value
    }
  })

  return answers
}

/**
 * Merges intake answers from different sources based on mode
 * Prioritizes answers from the intake answers table over interaction fields
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.intakeQuestions - Array of intake question objects
 * @param {Object} params.tableAnswers - Answers from the intake answers table (keyed by questionId)
 * @param {Object} params.interactionAnswers - Answers from interaction fields (keyed by questionId)
 * @param {string} params.mode - The intake answer mode ("new", "old", or "both")
 * @param {Function} params.parseAnswerValue - Function to parse answer values based on question type
 * @returns {Object} Merged answers keyed by questionId
 */
export const mergeIntakeAnswers = ({ intakeQuestions, tableAnswers, interactionAnswers, mode, parseAnswerValue }) => {
  const mergedAnswers = {}

  intakeQuestions.forEach((question) => {
    // Priority 1: Use intake answers table (if mode allows)
    if ((mode === "new" || mode === "both") && tableAnswers[question.id] !== undefined) {
      mergedAnswers[question.id] = parseAnswerValue(tableAnswers[question.id], question.itemType)
    }
    // Priority 2: Use interaction fields (if mode allows and table answer not available)
    else if ((mode === "old" || mode === "both") && interactionAnswers[question.id] !== undefined) {
      mergedAnswers[question.id] = interactionAnswers[question.id]
    }
  })

  return mergedAnswers
}

export const _formatAnswerValue = (value, itemType) => {
  if (value === null || value === undefined || value === "") {
    return ""
  }

  switch (itemType) {
    case "date":
      if (typeof value === "string") {
        return value
      }
      if (value instanceof Date) {
        return value.toISOString()
      }
      return String(value)
    case "boolean":
      return JSON.stringify(Boolean(value))
    case "text":
    default:
      return typeof value === "string" ? value : String(value)
  }
}

export const formatAnswers = (intakeQuestions, intakeAnswers) => {
  return intakeQuestions?.flatMap((question) => {
    const value = intakeAnswers[question.id]
    return [
      {
        questionId: question.id,
        answer: _formatAnswerValue(value, question.itemType),
      },
    ]
  })
}

export const formatAnswersRedacted = (intakeQuestions, intakeAnswers) => {
  return intakeQuestions?.map((question) => {
    const value = intakeAnswers[question.id]
    const formattedValue = _formatAnswerValue(value, question.itemType)
    const shouldRedact = REDACTABLE_INTAKE_QUESTION_TYPES.includes(question.itemType)

    return {
      questionId: question.id,
      questionTitle: question.title,
      itemType: question.itemType,
      answer: shouldRedact && formattedValue ? "**REDACTED**" : formattedValue,
    }
  })
}

export const parseAnswerValue = (storedAnswer, itemType) => {
  if (storedAnswer === null || storedAnswer === undefined || storedAnswer === "") {
    return storedAnswer
  }

  switch (itemType) {
    case "boolean":
      try {
        return JSON.parse(storedAnswer)
      } catch {
        return storedAnswer === "true" || storedAnswer === true
      }
    case "date":
      return storedAnswer
    case "text":
    default:
      return storedAnswer
  }
}
